import { useState } from "react";

/**
 * Shared avatar primitive — single source of truth for player avatars
 * across the app (top-bar profile button, profile page, admin inbox row,
 * club portal top-bar). Uses the user's uploaded avatar when available
 * and falls back to a 2-character initial badge with a brand gradient.
 *
 * Sizes match the existing call sites:
 *   sm  → w-8 h-8  (text-xs)        — used in tight rows
 *   md  → w-10 h-10 (text-sm)       — default, top-bar profile button
 *   lg  → w-12 h-12 (text-base)     — profile page header
 *   xl  → w-16 h-16 (text-lg)       — settings / clubs profile card
 *
 * Props:
 *   user            — { username?, email?, avatar_url? } (avatarUrl alias also accepted)
 *   size            — 'sm' | 'md' | 'lg' | 'xl' (default 'md')
 *   className       — wrapper classes (border / ring / etc.)
 *   data-testid     — forwarded to the wrapper for E2E targeting
 *
 * Resolves backend-relative `/uploads/...` paths into absolute URLs so the
 * <img> tag works regardless of which surface the avatar was uploaded
 * from.
 */
const UPLOAD_CACHE_BUSTER = "imgfix-20260518";

function withUploadCacheBuster(url) {
    if (!url || !/\/(?:api\/)?uploads\//.test(url)) return url;
    return `${url}${url.includes("?") ? "&" : "?"}v=${UPLOAD_CACHE_BUSTER}`;
}

export function resolveAvatarUrl(raw) {
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return withUploadCacheBuster(raw);
    if (/^data:/i.test(raw)) return raw;
    if (raw.startsWith("/")) {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return withUploadCacheBuster(`${base}${raw}`);
    }
    return withUploadCacheBuster(raw);
}

function deriveInitials(user) {
    const source = (
        user?.username ||
        user?.email ||
        user?.display_name ||
        ""
    ).trim();
    if (!source) return "?";
    // Use the first two alphanumerics from the display name. Falls back
    // to the first character if only one is present.
    const cleaned = source.replace(/[^a-zA-Z0-9]/g, "");
    if (!cleaned) return source.charAt(0).toUpperCase();
    if (cleaned.length === 1) return cleaned.charAt(0).toUpperCase();
    return (cleaned.charAt(0) + cleaned.charAt(1)).toUpperCase();
}

const SIZE_MAP = {
    sm: "w-8 h-8 text-[10px]",
    md: "w-10 h-10 text-xs",
    lg: "w-12 h-12 text-sm",
    xl: "w-16 h-16 text-base",
    podium: "w-16 h-16 text-lg",
    winner: "w-[76px] h-[76px] text-xl",
};

export default function UserAvatar({
    user,
    size = "md",
    className = "",
    bgClass = "bg-gradient-to-br from-violet-500/30 to-cyan-500/30",
    "data-testid": testId,
}) {
    const [failedSource, setFailedSource] = useState(null);
    const src = resolveAvatarUrl(user?.avatar_thumbnail_url || user?.avatar_url || user?.avatarUrl);
    const initials = deriveInitials(user);
    const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;
    const showImage = !!src && failedSource !== src;
    const pixels = {sm:32,md:40,lg:48,xl:64,podium:64,winner:76}[size] || 40;

    return (
        <div
            className={`relative ${sizeClass} rounded-full overflow-hidden flex items-center justify-center ${bgClass} ${className}`}
            data-testid={testId || "user-avatar"}
            style={{width:pixels,height:pixels,flexShrink:0}}
        >
            {showImage ? (
                <img
                    src={src}
                    alt={user?.username || "Avatar"}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    decoding="async"
                    width={pixels} height={pixels}
                    onError={() => setFailedSource(src)}
                />
            ) : (
                <span className="font-bold uppercase text-white tracking-wide select-none">
                    {initials}
                </span>
            )}
        </div>
    );
}
